import { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import { Card, CardHeader, CardBody, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import type { CommandCenter } from '@/services/marketingIntegrations.service';
import { tagCampaign } from './derive';

interface Props {
  cc: CommandCenter;
}

interface Task {
  id: string;
  title: string;
  owner: string;
  detail?: string;
}

interface Phase {
  range: string;
  label: string;
  tone: 'red' | 'amber' | 'emerald';
  tasks: Task[];
}

const STORAGE_KEY = 'ftp-roadmap-checks-v1';

function buildPhases(cc: CommandCenter, targetCpa: number): Phase[] {
  const cuts = (cc.campaigns?.rows ?? [])
    .map((c) => tagCampaign(c, targetCpa))
    .filter((c) => c.action === 'CUT' || c.action === 'FIX');

  const broken = (cc.contentPages?.rows ?? []).filter((p) => p.sessions >= 500 && p.conversions === 0);

  const titleFixes = (cc.gsc?.topQueries ?? []).filter(
    (q) => q.position <= 10 && q.ctr < 2 && q.impressions >= 200,
  );

  return [
    {
      range: 'Days 1–30',
      label: 'Stop the bleed · measurement before spend',
      tone: 'red',
      tasks: [
        { id: 'd30-1', title: 'Confirm GA4 + Google Ads + Meta + Search Console all show "Connected"', owner: 'CMO' },
        { id: 'd30-2', title: 'Audit GA4 conversion events — every primary CTA on the site has a tagged event', owner: 'Analytics' },
        { id: 'd30-3', title: 'Enable Google enhanced conversions and Meta CAPI server-side', owner: 'Engineering' },
        ...cuts.slice(0, 5).map((c, i) => ({
          id: `d30-cut-${i}`,
          title: `Pause: ${c.name}`,
          owner: 'CMO',
          detail: c.reason,
        })),
        ...broken.slice(0, 3).map((p, i) => ({
          id: `d30-broken-${i}`,
          title: `Add CTA + capture form on ${p.path}`,
          owner: 'CMO + Design',
          detail: `${p.sessions.toLocaleString()} sessions, 0 conversions`,
        })),
        { id: 'd30-9', title: 'Set targets per channel, write them down where the team can see them', owner: 'CMO' },
        { id: 'd30-10', title: 'Enable weekly Slack/email digest of dashboard KPIs', owner: 'Analytics' },
      ],
    },
    {
      range: 'Days 31–60',
      label: 'Build growth channels',
      tone: 'amber',
      tasks: [
        { id: 'd60-1', title: 'Launch the highest-impact CRO fix from Funnel & CRO tab', owner: 'CMO + Design' },
        ...titleFixes.slice(0, 3).map((q, i) => ({
          id: `d60-title-${i}`,
          title: `Rewrite title tag for "${q.query}"`,
          owner: 'SEO',
          detail: `#${q.position.toFixed(0)} · ${q.impressions.toLocaleString()} impressions · ${q.ctr.toFixed(1)}% CTR`,
        })),
        { id: 'd60-3', title: 'Build a head-to-head comparison page targeting top 3 competitors', owner: 'Marketing + Sales' },
        { id: 'd60-4', title: 'Sponsor 1 industry newsletter ($1.5–4K) with UTM-tagged landing page', owner: 'Marketing' },
        { id: 'd60-5', title: 'Add scroll + exit-intent email capture on the top 3 educational pages', owner: 'Marketing' },
        { id: 'd60-6', title: 'Wire a 6-stage lifecycle email automation; AI-generate 3 subject-line variants per stage', owner: 'Marketing + RevOps' },
        { id: 'd60-7', title: 'Launch a $50/day CTV / YouTube retargeting test against site visitors', owner: 'Paid Media' },
      ],
    },
    {
      range: 'Days 61–90',
      label: 'Scale what works · measure everything',
      tone: 'emerald',
      tasks: [
        { id: 'd90-1', title: 'Double the budget on the top SCALE-tagged campaign; re-evaluate weekly', owner: 'Paid Media' },
        { id: 'd90-2', title: 'Publish 1 YouTube video per top educational query — 12-week catalog ramp', owner: 'Content' },
        { id: 'd90-3', title: 'Run a competitor conquest paid campaign against the comparison page', owner: 'Paid Media' },
        { id: 'd90-4', title: 'A/B test direct mail to a 1,000-piece HNW segment with unique URL', owner: 'Growth' },
        { id: 'd90-5', title: 'Audit every page that hit "broken funnel" status 60 days ago — confirm CTAs in place', owner: 'Marketing' },
        { id: 'd90-6', title: 'Quarterly KPI review against targets in tab 8 — adjust budgets and roadmap', owner: 'CMO' },
        { id: 'd90-7', title: 'Document what worked; commit the playbook to the company wiki', owner: 'CMO' },
      ],
    },
  ];
}

const TONE_BG: Record<Phase['tone'], string> = {
  red: 'border-red-300 bg-red-50/50',
  amber: 'border-amber-300 bg-amber-50/50',
  emerald: 'border-emerald-300 bg-emerald-50/50',
};

const TONE_LABEL: Record<Phase['tone'], string> = {
  red: 'text-red-700',
  amber: 'text-amber-700',
  emerald: 'text-emerald-700',
};

export default function RoadmapTab({ cc }: Props) {
  const phases = buildPhases(cc, 200);

  // Persist checkbox state in localStorage
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setChecks(JSON.parse(raw));
    } catch {
      /* ignore parse errors */
    }
  }, []);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(checks)); } catch { /* full quota */ }
  }, [checks]);

  const toggle = (id: string) => setChecks((prev) => ({ ...prev, [id]: !prev[id] }));

  const totalTasks = phases.reduce((s, p) => s + p.tasks.length, 0);
  const doneTasks = phases.reduce((s, p) => s + p.tasks.filter((t) => checks[t.id]).length, 0);

  return (
    <div className="p-6 space-y-5">
      <div className="rounded-lg border border-slate-200 bg-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-blue-600" />
          <div>
            <div className="text-sm font-semibold text-slate-900">Execution roadmap</div>
            <div className="text-xs text-slate-500">Tasks derive from current dashboard findings; check off as completed.</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-slate-900 tabular-nums">{doneTasks} / {totalTasks}</div>
          <div className="text-xs text-slate-500">done</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {phases.map((phase) => {
          const phaseDone = phase.tasks.filter((t) => checks[t.id]).length;
          return (
            <Card key={phase.range} className={cn('border-2', TONE_BG[phase.tone])}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-3">
                  <span className={cn('font-bold', TONE_LABEL[phase.tone])}>{phase.range}</span>
                  <Badge variant="neutral" size="sm">{phaseDone} / {phase.tasks.length}</Badge>
                </CardTitle>
                <p className="text-xs text-slate-600 mt-1">{phase.label}</p>
              </CardHeader>
              <CardBody>
                <ul className="space-y-3">
                  {phase.tasks.map((t) => {
                    const done = !!checks[t.id];
                    return (
                      <li key={t.id} className="flex items-start gap-2.5 group">
                        <button
                          onClick={() => toggle(t.id)}
                          className={cn(
                            'flex-shrink-0 mt-0.5 h-4 w-4 rounded border-2 transition-colors',
                            done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 group-hover:border-slate-400 bg-white',
                          )}
                          aria-pressed={done}
                          aria-label={`Toggle ${t.title}`}
                        >
                          {done && (
                            <svg viewBox="0 0 16 16" className="h-3 w-3 text-white mx-auto"><path fill="currentColor" d="M6.5 12.5L2 8l1.5-1.5L6.5 9.5l6-6L14 5z"/></svg>
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className={cn('text-sm leading-snug', done ? 'text-slate-400 line-through' : 'text-slate-900')}>
                            {t.title}
                          </div>
                          {t.detail && (
                            <div className={cn('text-xs mt-0.5', done ? 'text-slate-400' : 'text-slate-500')}>{t.detail}</div>
                          )}
                          <div className="mt-1">
                            <Badge variant="info" size="sm">{t.owner}</Badge>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
